import { UserCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/features/auth';

import { useAssignComplaint, useComplaintAssignees } from '../api/complaints.queries';
import type { ComplaintDetails } from '../types/complaint.types';

const UNASSIGNED = 'none';

/**
 * Who handles the complaint (complaints.manage). Only an open complaint can be
 * given to someone; a closed one just says who had it.
 */
export function ComplaintAssignmentCard({ data }: { data: ComplaintDetails }) {
  const complaint = data.complaint;
  const isOpen = complaint.status === 'open';
  const { user } = useAuth();
  const assignees = useComplaintAssignees(complaint.id, isOpen);
  const assign = useAssignComplaint(complaint.id);

  const mine = user != null && complaint.assignedToUserId === user.id;
  const canTake = isOpen && !mine && (assignees.data ?? []).some((a) => a.id === user?.id);
  const currentMissing =
    complaint.assignedToUserId !== null &&
    !(assignees.data ?? []).some((a) => a.id === complaint.assignedToUserId);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Owner</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 text-xs">
        <Label htmlFor="complaint-assignee" className="text-muted-foreground text-xs font-normal">
          Assigned to
        </Label>
        {isOpen ? (
          <Select
            value={complaint.assignedToUserId ? String(complaint.assignedToUserId) : UNASSIGNED}
            onValueChange={(value) => assign.mutate(value === UNASSIGNED ? null : Number(value))}
            disabled={assign.isPending || assignees.isLoading}
          >
            <SelectTrigger id="complaint-assignee" size="sm" className="h-8 w-full text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNASSIGNED} className="text-xs">
                Unassigned
              </SelectItem>
              {/* Keep the current assignee visible even if no longer eligible. */}
              {currentMissing && (
                <SelectItem value={String(complaint.assignedToUserId)} className="text-xs">
                  {complaint.assigneeName ?? `#${complaint.assignedToUserId}`}
                </SelectItem>
              )}
              {(assignees.data ?? []).map((assignee) => (
                <SelectItem key={assignee.id} value={String(assignee.id)} className="text-xs">
                  {assignee.name ?? assignee.userName ?? `#${assignee.id}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <p id="complaint-assignee" className="font-medium">
            {complaint.assigneeName ?? 'Nobody'}
          </p>
        )}
        {canTake && user && (
          <Button
            size="sm"
            variant="outline"
            className="w-full"
            disabled={assign.isPending}
            onClick={() => assign.mutate(user.id)}
          >
            <UserCheck className="size-3.5" aria-hidden />
            Assign to me
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
